import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface DocumentType {
  id: number
  name: string
}

interface DocumentForm {
  name: string
  document_date: string | null
  customer_document_type_id: number | null
  notes: string
}

interface Props {
  customer: Customer
  document: DocumentForm
  document_types: DocumentType[]
  errors?: Record<string, string[]>
}

export default function New({
  customer,
  document,
  document_types,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Documents", href: `/customers/${customer.id}/documents` },
    { title: "New Document", href: `/customers/${customer.id}/documents/new` },
  ]

  const { data, setData, post, processing, transform } = useForm({
    name: document.name ?? "",
    document_date: document.document_date ?? "",
    customer_document_type_id: document.customer_document_type_id ?? "",
    notes: document.notes ?? "",
    file: null as File | null,
  })

  transform((data) => ({ customer_document: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/customers/${customer.id}/documents`, {
      forceFormData: true,
    })
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="New Document" />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">New Document</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              value={data.document_date ?? ""}
              onChange={(e) => setData("document_date", e.target.value)}
              className={inputClass}
            />
            {errors?.document_date && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.document_date[0]}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>Type</label>
            <select
              value={data.customer_document_type_id ?? ""}
              onChange={(e) =>
                setData(
                  "customer_document_type_id",
                  e.target.value ? e.target.value : "",
                )
              }
              className={inputClass}
            >
              <option value="">-- Select Type --</option>
              {document_types.map((dt) => (
                <option key={dt.id} value={dt.id}>
                  {dt.name}
                </option>
              ))}
            </select>
            {errors?.customer_document_type_id && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.customer_document_type_id[0]}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>Brief Description</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              maxLength={50}
              className={inputClass}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">{errors.name[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              rows={4}
              className={inputClass}
            />
            {errors?.notes && (
              <p className="text-hpi-red mt-1 text-sm">{errors.notes[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>File</label>
            <input
              type="file"
              onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
              className="text-hpi-med-gray file:bg-hpi-pale-blue file:text-hpi-dark-blue hover:file:bg-hpi-light-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
            />
            {errors?.file && (
              <p className="text-hpi-red mt-1 text-sm">{errors.file[0]}</p>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <Link
              href={`/customers/${customer.id}/documents`}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Uploading..." : "Save Document"}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
